import type {
  DashboardLimits,
  DashboardOverview,
  DashboardRepository,
  DeliveryReport,
  WriteResult,
} from "@estelle/domain";

import { ForbiddenError, apiFetch } from "@/adapters/api/base";

/**
 * The dashboard, over HTTP (#528).
 *
 * `apps/api/src/routes/dashboard.ts` is the other half of this file. The
 * answers are counts, sums, ages and medians: no order, shipment or customer
 * crosses this boundary, so nothing here can be logged that is customer data.
 */

const BASE = "/api/dashboard";

async function read<T>(path: string, label: string): Promise<T> {
  const response = await apiFetch(`${BASE}${path}`);
  if (response.status === 403) {
    const body = (await response.json().catch(() => null)) as {
      permission?: unknown;
    } | null;
    throw new ForbiddenError(
      `${BASE}${path}`,
      typeof body?.permission === "string" ? body.permission : undefined,
    );
  }
  if (!response.ok) {
    throw new Error(`${label} request failed: ${String(response.status)}`);
  }
  return (await response.json()) as T;
}

export function createApiDashboard(): DashboardRepository {
  return {
    overview: (period) =>
      read<DashboardOverview>(
        `/overview?${new URLSearchParams({ period }).toString()}`,
        "Dashboard",
      ),

    deliveryReport: (range) =>
      read<DeliveryReport>(
        `/delivery?${new URLSearchParams({ from: range.from, to: range.to }).toString()}`,
        "Delivery report",
      ),

    getLimits: async () =>
      (await read<{ limits: DashboardLimits }>("/limits", "Dashboard limits"))
        .limits,

    saveLimits: async (limits): Promise<WriteResult<DashboardLimits>> => {
      const response = await apiFetch(`${BASE}/limits`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ limits }),
      });
      const body = (await response.json().catch(() => ({}))) as {
        ok?: unknown;
        data?: DashboardLimits;
        rejection?: Extract<WriteResult<never>, { ok: false }>["rejection"];
        permission?: unknown;
      };

      if (response.status === 403) {
        return {
          ok: false,
          rejection: {
            kind: "forbidden",
            message: "You do not have permission to change these limits.",
            permission:
              typeof body.permission === "string"
                ? body.permission
                : "settings.company.manage",
          },
        };
      }
      if (body.ok === false && body.rejection) {
        return { ok: false, rejection: body.rejection };
      }
      if (response.ok && body.ok === true && body.data) {
        return { ok: true, data: body.data };
      }
      throw new Error(
        `Dashboard limits save failed: ${String(response.status)}`,
      );
    },
  };
}
